var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { BadgeCircle } from '@backrs_ui/common-ui/src/atoms/BadgeCircle';
import { AppIcon } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';
import { SENDER_TYPE } from '@backrs_ui/common-ui/src/models/lubav';
import { Box, Link, Typography, useMediaQuery, useTheme, Button, } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
var MESSAGE_STRINGS = appStrings.modules.lubav.message;
var NUGGET_TYPES = [
    'major',
    'career_path',
    'scholarship',
    'college',
    'skill',
    'story',
    'competency',
    'job_opportunity',
    'industry',
    'tool',
    'employer',
    'suggested_playbook',
    'coach',
    'deep_dive',
];
var NUGGET_HASH_PATTERN = /^#nugget:([^:]+):(.+)$/;
var DOCUMENT_HASH_PATTERN = /^#document:(.+)$/;
var SIGNUP_HASH = '#signup';
var preprocessNuggetReferences = function (text) {
    var nuggetTypesPattern = NUGGET_TYPES.join('|');
    var linkPattern = new RegExp("\\[([^\\]]+)\\]\\((".concat(nuggetTypesPattern, "):([^)]+)\\)"), 'g');
    var result = text.replace(linkPattern, function (_, linkText, nuggetType, nuggetId) {
        return "[".concat(linkText, "](#nugget:").concat(nuggetType, ":").concat(nuggetId, ")");
    });
    return result;
};
export var Message = function (_a) {
    var text = _a.text, additionalText = _a.additionalText, leading = _a.leading, component = _a.component, componentInsideMessage = _a.componentInsideMessage, actionButtons = _a.actionButtons, sender = _a.sender, onEdit = _a.onEdit, _b = _a.isEditing, isEditing = _b === void 0 ? false : _b, _c = _a.editingText, editingText = _c === void 0 ? '' : _c, onEditingTextChange = _a.onEditingTextChange, onEditSubmit = _a.onEditSubmit, onEditCancel = _a.onEditCancel, _d = _a.isNumeric, isNumeric = _d === void 0 ? false : _d, onNuggetReferenceClick = _a.onNuggetReferenceClick, isNuggetAvailable = _a.isNuggetAvailable, getNuggetHref = _a.getNuggetHref, onDocumentClick = _a.onDocumentClick, onSignupClick = _a.onSignupClick, contentRef = _a.contentRef;
    var processedText = preprocessNuggetReferences(text).replace(/\n(?!\n)/g, '  \n');
    var isMobile = useMediaQuery('(max-width: 900px)');
    var backrs = useTheme().backrs;
    var _e = useState(false), isExpanded = _e[0], setIsExpanded = _e[1];
    var canUserEdit = !isEditing && sender === SENDER_TYPE.USER;
    var showMessageBubble = isEditing || !!text.trim() || !!leading || (componentInsideMessage && !!component);
    var handleSubmit = function () {
        if (onEditSubmit) {
            onEditSubmit();
        }
    };
    var handleCancel = function () {
        if (onEditCancel) {
            onEditCancel();
        }
    };
    var handleKeyPress = function (event) {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handleSubmit();
        }
        else if (event.key === 'Escape') {
            handleCancel();
        }
    };
    var renderEditComponent = function () { return (_jsx(Box, { contentEditable: true, suppressContentEditableWarning: true, onInput: function (e) {
            var value = e.currentTarget.textContent || '';
            if (isNumeric) {
                onEditingTextChange === null || onEditingTextChange === void 0 ? void 0 : onEditingTextChange(value.replace(/[^0-9]/g, ''));
            }
            else {
                onEditingTextChange === null || onEditingTextChange === void 0 ? void 0 : onEditingTextChange(value);
            }
        }, onKeyDown: handleKeyPress, sx: editBoxSx, ref: function (el) {
            if (el && editingText !== el.textContent) {
                el.textContent = editingText;
                setTimeout(function () {
                    var selection = window.getSelection();
                    var range = document.createRange();
                    range.selectNodeContents(el);
                    range.collapse(false);
                    selection === null || selection === void 0 ? void 0 : selection.removeAllRanges();
                    selection === null || selection === void 0 ? void 0 : selection.addRange(range);
                }, 0);
            }
            if (el) {
                el.focus();
            }
        } })); };
    var renderMessageContent = function () { return (_jsxs(Box, { ref: contentRef, sx: messageContentSx, children: [_jsx(ReactMarkdown, { remarkPlugins: [remarkGfm], components: {
                    a: function (_a) {
                        var href = _a.href, children = _a.children;
                        var nuggetMatch = href ? NUGGET_HASH_PATTERN.exec(href) : null;
                        if (nuggetMatch) {
                            var nuggetType_1 = nuggetMatch[1], nuggetId_1 = nuggetMatch[2];
                            if (isNuggetAvailable && !isNuggetAvailable(nuggetType_1, nuggetId_1)) {
                                return _jsx(_Fragment, { children: children });
                            }
                            var nuggetHref = getNuggetHref === null || getNuggetHref === void 0 ? void 0 : getNuggetHref(nuggetType_1, nuggetId_1);
                            if (nuggetHref) {
                                return (_jsx(Link, { id: "nugget-ref-".concat(nuggetType_1, "-").concat(nuggetId_1), href: nuggetHref, "data-copy-style": JSON.stringify({
                                        color: backrs.colors['Blue/500'],
                                        textDecoration: 'underline',
                                    }), sx: nuggetReferenceStyle, onClick: function (event) {
                                        event.preventDefault();
                                        onNuggetReferenceClick === null || onNuggetReferenceClick === void 0 ? void 0 : onNuggetReferenceClick(nuggetType_1, nuggetId_1);
                                    }, children: children }));
                            }
                            return (_jsx(Box, { id: "nugget-ref-".concat(nuggetType_1, "-").concat(nuggetId_1), component: "span", sx: nuggetReferenceStyle, onClick: function () { return onNuggetReferenceClick === null || onNuggetReferenceClick === void 0 ? void 0 : onNuggetReferenceClick(nuggetType_1, nuggetId_1); }, children: children }));
                        }
                        if (href === SIGNUP_HASH) {
                            if (!onSignupClick) {
                                return _jsx(_Fragment, { children: children });
                            }
                            return (_jsx(Link, { id: "message-signup-link", component: "span", sx: documentLinkStyle, onClick: onSignupClick, children: children }));
                        }
                        var documentMatch = href ? DOCUMENT_HASH_PATTERN.exec(href) : null;
                        if (documentMatch) {
                            var documentType_1 = documentMatch[1];
                            return (_jsx(Link, { id: "message-document-link", component: "span", sx: documentLinkStyle, onClick: function () { return onDocumentClick === null || onDocumentClick === void 0 ? void 0 : onDocumentClick(documentType_1); }, children: children }));
                        }
                        return (_jsx(Link, { href: href, target: "_blank", rel: "noopener noreferrer", sx: linkStyle, children: children }));
                    },
                    h1: function (_a) {
                        var children = _a.children;
                        return (_jsx(Typography, { variant: "text-xl-semibold", sx: headingStyle, children: children }));
                    },
                    h2: function (_a) {
                        var children = _a.children;
                        return (_jsx(Typography, { variant: "text-lg-semibold", sx: headingStyle, children: children }));
                    },
                    h3: function (_a) {
                        var children = _a.children;
                        return (_jsx(Typography, { variant: "text-md-semibold", sx: headingStyle, children: children }));
                    },
                    h4: function (_a) {
                        var children = _a.children;
                        return (_jsx(Typography, { variant: "text-sm-semibold", sx: headingStyle, children: children }));
                    },
                    h5: function (_a) {
                        var children = _a.children;
                        return (_jsx(Typography, { variant: "text-sm-semibold", sx: headingStyle, children: children }));
                    },
                    h6: function (_a) {
                        var children = _a.children;
                        return (_jsx(Typography, { variant: "text-sm-semibold", sx: headingStyle, children: children }));
                    },
                    blockquote: function (_a) {
                        var children = _a.children;
                        return _jsx(Box, { sx: blockquoteStyle, children: children });
                    },
                    table: function (_a) {
                        var children = _a.children;
                        return (_jsx(Box, { sx: tableWrapperStyle, children: _jsx(Box, { component: "table", sx: tableStyle, children: children }) }));
                    },
                    thead: function (_a) {
                        var children = _a.children;
                        return (_jsx(Box, { component: "thead", sx: theadStyle, children: children }));
                    },
                    th: function (_a) {
                        var children = _a.children;
                        return (_jsx(Box, { component: "th", sx: thStyle, children: children }));
                    },
                    td: function (_a) {
                        var children = _a.children;
                        return (_jsx(Box, { component: "td", sx: tdStyle, children: children }));
                    },
                    code: function (_a) {
                        var children = _a.children, className = _a.className;
                        var isBlock = className === null || className === void 0 ? void 0 : className.startsWith('language-');
                        return isBlock ? (_jsx(Box, { sx: codeBlockStyle, children: _jsx(Box, { component: "code", sx: codeTextStyle, children: children }) })) : (_jsx(Box, { component: "code", sx: inlineCodeStyle, children: children }));
                    },
                    pre: function (_a) {
                        var children = _a.children;
                        return _jsx(_Fragment, { children: children });
                    },
                    ul: function (_a) {
                        var children = _a.children;
                        return _jsx("ul", { style: listStyle, children: children });
                    },
                    ol: function (_a) {
                        var children = _a.children, start = _a.start;
                        return (_jsx("ol", { style: listStyle, start: start, children: children }));
                    },
                    li: function (_a) {
                        var children = _a.children;
                        return _jsx("li", { style: listItemStyle(isMobile), children: children });
                    },
                    p: function (_a) {
                        var children = _a.children;
                        return _jsx("p", { style: paragraphStyle, children: children });
                    },
                }, children: additionalText && isExpanded
                    ? "".concat(processedText, "\n\n").concat(preprocessNuggetReferences(additionalText))
                    : processedText }), additionalText && (_jsx(Typography, { onClick: function () { return setIsExpanded(function (prev) { return !prev; }); }, sx: readMoreStyle, variant: "text-md-semibold", children: isExpanded ? MESSAGE_STRINGS.lessContext : MESSAGE_STRINGS.moreContext }))] })); };
    return (_jsxs(Box, { sx: containerSx(sender), className: "message-container", children: [sender === SENDER_TYPE.USER && (_jsx(Typography, { variant: "text-sm-medium", children: MESSAGE_STRINGS.you })), _jsxs(Box, { className: "message-content", sx: messageContentWrapperSx, children: [_jsxs(Box, { sx: messageRowSx(sender), children: [sender === SENDER_TYPE.AI && (_jsx(Box, { sx: badgeCircleOffsetSx, children: _jsx(BadgeCircle, { size: "lg" }) })), canUserEdit && isMobile && (_jsx(Box, { onClick: onEdit, sx: editIconSx(isMobile), children: _jsx(AppIcon, { iconName: "Edit01", diameter: "16px", color: "Gray/500", strokeWidth: "bold" }) })), showMessageBubble && (_jsxs(Box, { sx: messageContainerSx(sender), children: [leading, isEditing ? renderEditComponent() : renderMessageContent(), componentInsideMessage && component] }, isEditing ? 'editing' : 'viewing'))] }), actionButtons && _jsx(Box, { sx: actionButtonsContainerSx, children: actionButtons })] }), !componentInsideMessage && component && (_jsx(Box, { sx: componentContainerSx(sender), children: component })), canUserEdit && !isMobile && (_jsx(Box, { onClick: onEdit, sx: editIconSx(isMobile), children: _jsx(AppIcon, { iconName: "Edit01", diameter: "16px", color: "Gray/500", strokeWidth: "bold" }) })), isEditing && (_jsxs(Box, { sx: editButtonsSx, children: [_jsx(Button, { id: "message-edit-submit-button", size: "sm", onClick: handleSubmit, hierarchy: "Primary", children: MESSAGE_STRINGS.submit }), _jsx(Button, { id: "message-edit-cancel-button", size: "sm", onClick: handleCancel, hierarchy: "Secondary gray", children: MESSAGE_STRINGS.cancel })] }))] }));
};
var containerSx = function (sender) {
    return function (_a) {
        var backrs = _a.backrs;
        return ({
            display: 'flex',
            flexDirection: 'column',
            alignItems: sender === SENDER_TYPE.USER ? 'flex-end' : 'flex-start',
            justifyContent: sender === SENDER_TYPE.USER ? 'flex-end' : 'flex-start',
            gap: backrs.spacing['spacing-xs'],
            width: '100%',
        });
    };
};
var messageRowSx = function (sender) {
    return function (_a) {
        var backrs = _a.backrs;
        return ({
            display: 'flex',
            alignItems: sender === SENDER_TYPE.USER ? 'flex-end' : 'flex-start',
            justifyContent: sender === SENDER_TYPE.USER ? 'flex-end' : 'flex-start',
            gap: backrs.spacing['spacing-md'],
            width: '100%',
            minWidth: 0,
        });
    };
};
var badgeCircleOffsetSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        position: 'relative',
        top: backrs.spacing['spacing-xxs'],
    });
};
var headingStyle = {
    margin: 0,
};
var tableWrapperStyle = {
    overflowX: 'auto',
    width: '100%',
};
var tableStyle = function (_a) {
    var backrs = _a.backrs;
    return ({
        width: '100%',
        borderCollapse: 'collapse',
        borderRadius: backrs.radius['radius-sm'],
        overflow: 'hidden',
    });
};
var theadStyle = function (_a) {
    var backrs = _a.backrs;
    return ({
        backgroundColor: backrs.colors['Gray/100'],
    });
};
var thStyle = function (_a) {
    var backrs = _a.backrs, typography = _a.typography;
    return (__assign(__assign({}, typography['text-sm-semibold']), { padding: "".concat(backrs.spacing['spacing-sm'], " ").concat(backrs.spacing['spacing-md']), textAlign: 'left', borderBottom: "1px solid ".concat(backrs.colors['Gray/200']) }));
};
var tdStyle = function (_a) {
    var backrs = _a.backrs, typography = _a.typography;
    return (__assign(__assign({}, typography['text-sm-regular']), { padding: "".concat(backrs.spacing['spacing-sm'], " ").concat(backrs.spacing['spacing-md']), borderBottom: "1px solid ".concat(backrs.colors['Gray/100']) }));
};
var codeBlockStyle = function (_a) {
    var backrs = _a.backrs;
    return ({
        backgroundColor: backrs.colors['Gray/100'],
        borderRadius: backrs.radius['radius-sm'],
        padding: backrs.spacing['spacing-md'],
        overflowX: 'auto',
    });
};
var codeTextStyle = function (_a) {
    var typography = _a.typography;
    return (__assign(__assign({}, typography['text-sm-regular']), { whiteSpace: 'pre' }));
};
var inlineCodeStyle = function (_a) {
    var backrs = _a.backrs, typography = _a.typography;
    return (__assign(__assign({}, typography['text-sm-regular']), { backgroundColor: backrs.colors['Gray/100'], borderRadius: backrs.radius['radius-xs'], padding: "".concat(backrs.spacing['spacing-xxs'], " ").concat(backrs.spacing['spacing-xs']) }));
};
var blockquoteStyle = function (_a) {
    var backrs = _a.backrs;
    return ({
        borderLeft: "3px solid ".concat(backrs.colors['Brand/300']),
        paddingLeft: backrs.spacing['spacing-md'],
        margin: 0,
        fontStyle: 'italic',
        opacity: 0.85,
    });
};
var listStyle = {
    paddingLeft: '24px',
    margin: 0,
};
var paragraphStyle = {
    margin: 0,
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
};
var listItemStyle = function (isMobile) { return ({
    marginBottom: isMobile ? '2px' : '4px',
    marginTop: isMobile ? '2px' : '4px',
}); };
var linkStyle = function (_a) {
    var backrs = _a.backrs;
    return ({
        color: backrs.colors['Blue/500'],
        fontWeight: 'bold',
    });
};
var documentLinkStyle = function (_a) {
    var backrs = _a.backrs;
    return ({
        color: backrs.colors['Blue/500'],
        fontWeight: 'bold',
        cursor: 'pointer',
        textDecoration: 'underline',
        '&:hover': {
            opacity: 0.7,
        },
    });
};
var nuggetReferenceStyle = function (_a) {
    var backrs = _a.backrs;
    return ({
        fontWeight: 'bold',
        cursor: 'pointer',
        color: 'inherit',
        textDecoration: 'none',
        borderBottom: "1px dashed ".concat(backrs.colors['Brand/500']),
        transition: 'background-color 0.15s ease-in-out',
        '&:hover': {
            backgroundColor: backrs.colors['Brand/100'],
        },
    });
};
var readMoreStyle = function (_a) {
    var backrs = _a.backrs;
    return ({
        cursor: 'pointer',
        width: 'fit-content',
        color: backrs.colors['Blue/500'],
        '&:hover': {
            opacity: 0.7,
        },
    });
};
var messageContentSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: 'flex',
        flexDirection: 'column',
        gap: backrs.spacing['spacing-lg'],
        overflowWrap: 'break-word',
        wordBreak: 'break-word',
        minWidth: 0,
    });
};
var messageContainerSx = function (sender) {
    return function (_a) {
        var backrs = _a.backrs, typography = _a.typography;
        return (__assign(__assign({ display: 'flex', flexDirection: 'column', width: 'fit-content', maxWidth: '100%', minWidth: 0, overflowWrap: 'break-word', wordBreak: 'break-word' }, typography['text-md-regular']), (sender === SENDER_TYPE.USER
            ? {
                alignItems: 'flex-end',
                justifyContent: 'flex-end',
                color: backrs.colors['Colors/Text/text-white'],
                backgroundColor: backrs.colors['Brand/500'],
                border: "1px solid ".concat(backrs.colors['Beige/950']),
                padding: "".concat(backrs.spacing['spacing-md'], " ").concat(backrs.spacing['spacing-lg']),
                borderRadius: "".concat(backrs.radius['radius-md'], " 0 ").concat(backrs.radius['radius-md'], " ").concat(backrs.radius['radius-md']),
            }
            : {
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                color: backrs.colors['Colors/Text/text-primary'],
            })));
    };
};
var messageContentWrapperSx = {
    width: '100%',
    minWidth: 0,
};
var actionButtonsContainerSx = {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
    maxHeight: { xs: '40px', md: 0 },
    overflow: 'hidden',
    transition: 'max-height 0.2s ease-in-out',
    '.message-content:hover &': {
        maxHeight: '40px',
    },
};
var componentContainerSx = function (sender) {
    return function (_a) {
        var backrs = _a.backrs;
        var isUser = sender === SENDER_TYPE.USER;
        var inset = "calc(".concat(backrs.spacing['spacing-3xl'], " + ").concat(backrs.spacing['spacing-xs'], ")");
        return {
            display: 'flex',
            flexDirection: 'column',
            alignItems: isUser ? 'flex-end' : 'flex-start',
            ml: isUser ? 0 : inset,
            pr: isUser ? 0 : inset,
            width: '100%',
        };
    };
};
var editButtonsSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: 'flex',
        gap: backrs.spacing['spacing-sm'],
        justifyContent: 'flex-end',
        marginTop: backrs.spacing['spacing-xs'],
    });
};
var editBoxSx = function (_a) {
    var backrs = _a.backrs, typography = _a.typography;
    return (__assign(__assign({}, typography['text-md-regular']), { color: backrs.colors['Colors/Text/text-white'], backgroundColor: backrs.colors['Colors/Base/Transparent'], border: 'none', outline: 'none', width: 'fit-content', minWidth: '20px', maxWidth: '100%', whiteSpace: 'pre-wrap', wordWrap: 'break-word', cursor: 'text', '&:focus': {
            outline: 'none',
        } }));
};
var editIconSx = function (isMobile) {
    return function (_a) {
        var backrs = _a.backrs;
        return (__assign({ cursor: 'pointer', '&:hover': {
                opacity: 0.5,
                backgroundColor: backrs.colors['Colors/Base/Transparent'],
            } }, (!isMobile && {
            opacity: 0,
            visibility: 'hidden',
            transition: 'opacity 0.2s ease-in-out, visibility 0.2s ease-in-out',
            '.message-container:hover &': {
                opacity: 1,
                visibility: 'visible',
            },
        })));
    };
};
